import { Indicator, Root } from '@radix-ui/react-progress';
import { forwardRef, type ComponentPropsWithoutRef, type ElementRef } from 'react';

import { cn } from '@/shared/utils/cn/cn.util';

export type ProgressProps = ComponentPropsWithoutRef<typeof Root>;

const MIN = 0;
const MAX = 100;

/**
 * The upload/import progress bar.
 *
 * Two behaviours worth naming. **Clamping**: a byte counter that reports more
 * bytes than it declared (retries, chunk overlap) would otherwise push the fill
 * past the track and out of the card. **Indeterminate**: `value === undefined`
 * stays indeterminate rather than collapsing to 0%, so "started, size unknown"
 * is distinguishable from "nothing has happened".
 *
 * The indeterminate state renders a PULSING SLIVER, not a full track. Reusing
 * the determinate fill for it produced a bar sitting at 100% while the upload
 * had not started — the single most misleading thing a progress bar can do, and
 * invisible to the ARIA assertion, which was already correct.
 *
 * The fill grows from the inline START, so it fills right-to-left in Arabic.
 */
export const Progress = forwardRef<ElementRef<typeof Root>, ProgressProps>(function Progress(
  { className, value, ...props },
  ref,
) {
  const clamped =
    value === null || value === undefined ? undefined : Math.min(MAX, Math.max(MIN, value));

  return (
    <Root
      ref={ref}
      value={clamped ?? null}
      max={MAX}
      className={cn('relative h-2 w-full overflow-hidden rounded-full bg-secondary', className)}
      {...props}
    >
      {clamped === undefined ? (
        <Indicator
          data-testid="progress-indeterminate"
          className="h-full w-1/3 animate-pulse rounded-full bg-primary"
        />
      ) : (
        <Indicator
          data-testid="progress-fill"
          className="h-full w-full bg-primary transition-transform duration-DEFAULT ease-standard"
          /*
           * The sign comes from `--inline-start-sign`, which `globals.css` flips
           * to `-1` under `[dir='rtl']` — the same mechanism the sheet's slide
           * animations use.
           *
           * It used to be a `rtl:-scale-x-100` CLASS beside this inline
           * `transform`, and that never worked: an inline style replaces the
           * class's `transform` outright rather than composing with it, so the
           * mirror silently did nothing and an Arabic bar filled from the left —
           * i.e. from the END. Invisible to the ARIA assertion, which was always
           * correct, and to every unit test, because jsdom computes no layout.
           * The P10.2f walk measured the fill against its track and found it.
           */
          style={{
            transform: `translateX(calc(-${String(MAX - clamped)}% * var(--inline-start-sign, 1)))`,
          }}
        />
      )}
    </Root>
  );
});
